// Step 3 (REVEAL) + step 5 (EFFECTS): per-cell VALUE with a breakdown (docs/04 §2, §4, §11).
// Pure: no RNG streams are advanced. Permanent effects are queued as `Pending` and applied on bank.
import type { EvalCtx, GridCellView, GridView, SymbolDef } from '../content/defs';
import { SYMBOLS } from '../content/registry';
import type { Modifiers } from './modifiers';
import { cellRoll } from './rng';
import type { Cell, SymbolInstance } from './types';

export type Pending =
  | { kind: 'destroy'; uid: number; by: string }
  | { kind: 'grow'; uid: number; n: number }
  | { kind: 'hunger'; n: number }
  | { kind: 'spawn'; defId: string; chance: number; by: string }
  | { kind: 'freeCharm' }
  | { kind: 'cleanse' };

export interface ValueStep {
  source: string;
  op: 'base' | 'add' | 'mul' | 'set' | 'pct';
  amount: number;
}

export interface CellValues {
  values: number[][];
  steps: ValueStep[][][];
  pending: Pending[];
  /** Instances next to a guard (Spider): they can't be destroyed or eaten. */
  guarded: Set<number>;
}

const FALLBACK: SymbolDef = { id: 'cherry', tags: ['fruit'], rarity: 'C', baseValue: 1, sprite: 'sym_cherry', gate: 'start' };

export function makeGridView(uids: number[][], deck: Map<number, SymbolInstance>, rows: number): GridView {
  const reels = uids.length;
  const cells: (GridCellView | null)[][] = uids.map((col, reel) =>
    col.map((uid, row) => {
      const inst = deck.get(uid);
      if (!inst) return null;
      return { cell: { reel, row }, inst, def: SYMBOLS.get(inst.defId) ?? FALLBACK };
    }),
  );
  const at = (reel: number, row: number): GridCellView | null =>
    reel < 0 || row < 0 || reel >= reels || row >= rows ? null : (cells[reel]?.[row] ?? null);
  const all = (): GridCellView[] => {
    const out: GridCellView[] = [];
    for (let y = 0; y < rows; y++) for (let x = 0; x < reels; x++) {
      const v = at(x, y);
      if (v) out.push(v);
    }
    return out;
  };
  const within = (c: Cell, r: number): GridCellView[] => {
    const out: GridCellView[] = [];
    for (let dy = -r; dy <= r; dy++)
      for (let dx = -r; dx <= r; dx++) {
        if (!dx && !dy) continue;
        const v = at(c.reel + dx, c.row + dy);
        if (v) out.push(v);
      }
    return out;
  };
  const neighbors = (c: Cell): GridCellView[] => within(c, 1);
  return { reels, rows, at, all, neighbors, within };
}

export interface ValueOpts {
  seed: string;
  spinIndex: number;
  held: boolean[];
  hunger: number;
  doom: number;
}

export function computeCellValues(view: GridView, mods: Modifiers, opts: ValueOpts): CellValues {
  const values: number[][] = [];
  const steps: ValueStep[][][] = [];
  const pending: Pending[] = [];
  for (let x = 0; x < view.reels; x++) {
    values.push(new Array(view.rows).fill(0));
    steps.push(Array.from({ length: view.rows }, () => []));
  }
  const log = (c: Cell, s: ValueStep) => steps[c.reel]![c.row]!.push(s);
  const guarded = new Set<number>();
  for (const v of view.all()) if (v.def.guard) for (const n of view.neighbors(v.cell)) guarded.add(n.inst.uid);
  const vermin = mods.verminEconomy ? view.all().filter((v) => v.def.tags.includes('vermin')).length : 0;

  // Base VALUE + flat modifiers.
  for (const v of view.all()) {
    const { cell, inst, def } = v;
    let base = def.baseValue;
    if (def.roll) {
      const lo = Math.max(def.roll[0], Math.min(def.roll[1], mods.diceMin));
      const r = cellRoll(opts.seed, opts.spinIndex, cell.reel, cell.row, inst.uid);
      base = r.range(lo, def.roll[1]);
      if (mods.diceAdvantage) base = Math.max(base, r.range(lo, def.roll[1]));
    }
    let val = base;
    log(cell, { source: def.id, op: 'base', amount: base });
    if (def.inert) {
      values[cell.reel]![cell.row] = val;
      continue;
    }
    if (inst.bonusValue) {
      val += inst.bonusValue;
      log(cell, { source: 'growth', op: 'add', amount: inst.bonusValue });
    }
    let flat = mods.valueAll + (mods.valueById[def.id] ?? 0);
    for (const t of def.tags) flat += mods.valueByTag[t] ?? 0;
    if (opts.held[cell.reel]) flat += mods.heldValue;
    if (vermin && def.tags.includes('vermin')) flat += vermin * mods.verminEconomy;
    if (flat) {
      val += flat;
      log(cell, { source: 'modifiers', op: 'add', amount: flat });
    }
    values[cell.reel]![cell.row] = val;
  }

  const ctxFor = (self: GridCellView, phase: string): EvalCtx => ({
    self,
    grid: view,
    hunger: opts.hunger,
    doom: opts.doom,
    mods,
    value: (c) => values[c.reel]?.[c.row] ?? 0,
    add: (c, n) => {
      if (!view.at(c.reel, c.row)) return;
      values[c.reel]![c.row]! += n;
      log(c, { source: self.def.id, op: 'add', amount: n });
    },
    mul: (c, x) => {
      if (!view.at(c.reel, c.row)) return;
      values[c.reel]![c.row]! *= x;
      log(c, { source: self.def.id, op: 'mul', amount: x });
    },
    set: (c, n) => {
      if (!view.at(c.reel, c.row)) return;
      values[c.reel]![c.row] = n;
      log(c, { source: self.def.id, op: 'set', amount: n });
    },
    destroyLater: (uid, by) => {
      if (guarded.has(uid)) return false;
      if (!pending.some((p) => p.kind === 'destroy' && p.uid === uid)) pending.push({ kind: 'destroy', uid, by: by || phase });
      return true;
    },
    growLater: (uid, n) => pending.push({ kind: 'grow', uid, n }),
    spawnLater: (defId, chance, by) => pending.push({ kind: 'spawn', defId, chance, by }),
  });

  // Effects in reading order, phased: additive auras → multipliers → copies.
  const cells = view.all();
  for (const v of cells) v.def.add?.(ctxFor(v, 'add'));
  for (const v of cells) v.def.mul?.(ctxFor(v, 'mul'));
  for (const v of cells) v.def.copy?.(ctxFor(v, 'copy'));

  // Family %-bonuses, summed within the symbol, applied as one multiplier; then ×VALUE sources.
  for (const v of cells) {
    let pct = mods.pctByRarity[v.def.rarity ?? 'C'] ?? 0;
    if (!v.def.rarity) pct = 0;
    if (v.def.inert) continue;
    for (const t of v.def.tags) pct += mods.pctByTag[t] ?? 0;
    const c = v.cell;
    if (pct) {
      values[c.reel]![c.row]! *= 1 + pct;
      log(c, { source: 'family', op: 'pct', amount: pct });
    }
    let x = mods.valueXById[v.def.id] ?? 1;
    for (const t of v.def.tags) x *= mods.valueXByTag[t] ?? 1;
    if (mods.halveExceptTags && !v.def.tags.some((t) => mods.halveExceptTags!.includes(t))) x *= 0.5;
    if (x !== 1) {
      values[c.reel]![c.row]! *= x;
      log(c, { source: 'xvalue', op: 'mul', amount: x });
    }
    if (values[c.reel]![c.row]! < 0) values[c.reel]![c.row] = 0;
  }

  return { values, steps, pending, guarded };
}
